import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    signal,
    TemplateRef,
    ViewChild,
    WritableSignal,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { LayoutService } from '@cvx/nextpage';
import { NgbModal, NgbModalRef, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { delay, map, Observable, retry, startWith, switchMap, tap, timer } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { BusinessDomain } from '../../common/models/business-domain.model';
import { UserGraph } from '../../common/models/user-graph.model';
import { UserGraphAutocompleteComponent } from '../../common/user-graph-autocomplete/user-graph-autocomplete.component';
import { BusinessDomainService } from '../business-domain.service';
import {
    Capability,
    CreateBusinessDomainRequest,
    CreateBusinessUnitForm,
    Platform,
} from './models/business-domain.model-creation';

@Component({
    standalone: true,
    selector: 'app-bd-creation',
    imports: [
        NgbModule,
        FormsModule,
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        UserGraphAutocompleteComponent,
    ],
    templateUrl: './bd-creation.component.html',
    styleUrls: ['./bd-creation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdCreationComponent implements OnInit {
    @ViewChild('provisioningModal') protected provisioningModal!: TemplateRef<unknown>;

    @ViewChild('errorModal') protected errorModal!: TemplateRef<unknown>;

    protected createBusinessForm: FormGroup<CreateBusinessUnitForm>;

    protected currentUser = signal<UserGraph | null>(null);

    protected businessUnits: WritableSignal<string[]> = signal([]);

    protected platforms: WritableSignal<Record<string, Platform>> = signal({}); // Platform Name as key

    protected capabilities: WritableSignal<Record<string, Capability[]>> = signal({}); // Platform Name as key

    protected capabilities$!: Observable<Capability[]>;

    protected activeBusinessDomain: WritableSignal<BusinessDomain | null> = signal(null);

    protected provisioningDone = signal(false);

    private modalRef?: NgbModalRef;

    protected errorMessage = signal('');

    public constructor(
        private readonly layoutService: LayoutService,
        private readonly businessDomainService: BusinessDomainService,
        private readonly authService: AuthService,
        private readonly fb: FormBuilder,
        private readonly modalService: NgbModal,
    ) {
        this.createBusinessForm = this.fb.nonNullable.group({
            businessUnit: this.fb.nonNullable.control<string>(''),
            platform: this.fb.nonNullable.control<string>('', { validators: [Validators.required] }),
            capability: this.fb.nonNullable.control<string>('', { validators: [Validators.required] }),
            subGroup: this.fb.nonNullable.control<string>(''),
            firstOwner: this.fb.nonNullable.control<UserGraph | null>(null, { validators: [Validators.required] }),
            secondOwner: this.fb.nonNullable.control<string | UserGraph>('', { validators: [Validators.required] }),
        });
    }

    public ngOnInit(): void {
        this.layoutService.setClass('layout-12');
        this.fetchBusinessDomainOptions();
        this.setCurrentUserAsFirstOwner();

        this.capabilities$ = this.createBusinessForm.controls.platform.valueChanges.pipe(
            map((platformName) => this.capabilities()[platformName] ?? []),
            startWith([]),
        );

        const capabilityInputEnabled$ = this.createBusinessForm.controls.platform.valueChanges.pipe(
            map((platform) => !!platform),
            startWith(false),
            tap((enabled) =>
                enabled
                    ? this.createBusinessForm.controls.capability.enable()
                    : this.createBusinessForm.controls.capability.disable(),
            ),
        );
        capabilityInputEnabled$.subscribe();
    }

    protected hasFieldError(fieldName: string, errorType: string = 'required'): boolean {
        const control = this.createBusinessForm.get(fieldName);
        return !!(control?.hasError(errorType) && control?.touched);
    }

    public onSubmit(): void {
        if (!this.createBusinessForm.valid) {
            this.createBusinessForm.markAllAsTouched();
            return;
        }

        this.provisioningDone.set(false);
        this.modalRef = this.modalService.open(this.provisioningModal, {
            backdrop: 'static',
            keyboard: false,
            windowClass: 'wider-modal',
        });

        const formValue = this.createBusinessForm.getRawValue();

        const firstOwner = formValue.firstOwner;
        if (!firstOwner) {
            throw new Error("The first owner should have already been set by now (it's set on page load).");
        }

        const secondOwner = formValue.secondOwner as UserGraph;
        const businessDomain: CreateBusinessDomainRequest = {
            unit: formValue.businessUnit != 'None' ? formValue.businessUnit : '',
            platform: formValue.platform,
            capability: formValue.capability,
            subGroup: formValue.subGroup.trim(),
            owners: [firstOwner.id, secondOwner.id],
            isDraft: false,
        };

        this.businessDomainService
            .createBusinessDomain(businessDomain)
            .pipe(
                delay(20000),
                switchMap((response) => {
                    return this.getCreatedBusinessDomain(response.id).pipe(
                        tap((response) => {
                            if (response.state === 'Pending') {
                                throw new Error(`
                                    This is a temporary error that we throw only to trigger the retry logic
                                    (we catch this error in the retry() operator), so that we keep making
                                    requests until the Business Domain is provisioned.
                                `);
                            }
                        }),
                        // This retry catches two errors:
                        // - If the Business Domain's state is "Pending", it catches the error that we threw in the previous tap().
                        // - If the Business Domain has just been provisioned, sometimes the API throws an error because the Entra Groups
                        //   haven't been fully configured yet, so this also catches that error.
                        retry({
                            count: 20,
                            delay: (error, retryCount) => {
                                const delayMs = retryCount * 1000;
                                return timer(delayMs);
                            },
                        }),
                    );
                }),
            )
            .subscribe({
                next: (response) => {
                    this.activeBusinessDomain.set(response);
                    this.provisioningDone.set(true);
                    this.resetForm();
                },
                error: (err) => {
                    console.error('Failed to create business domain:', err);
                    this.modalRef?.close(); // Close provisioning modal
                    this.errorMessage.set(err?.error ? JSON.stringify(err.error) : err.message || err);
                    this.modalService.open(this.errorModal, { backdrop: 'static', keyboard: false });
                },
            });
    }

    public getCreatedBusinessDomain(id: string): Observable<BusinessDomain> {
        return this.businessDomainService.getBusinessDomainDetails(id);
    }

    public getPlatformNames(): string[] {
        return Object.keys(this.platforms());
    }

    public resetForm(): void {
        this.createBusinessForm.reset();
        this.createBusinessForm.controls.firstOwner.setValue(this.currentUser());
    }

    private fetchBusinessDomainOptions(): void {
        this.businessDomainService.getBusinessDomainOptions().subscribe({
            next: (options) => {
                this.businessUnits.set(options.units);

                this.platforms.set(
                    Object.fromEntries(
                        options.platforms.map((platform) => [platform.platform.name, platform.platform]),
                    ),
                );

                this.capabilities.set(
                    Object.fromEntries(
                        options.platforms.map((platform) => [platform.platform.name, platform.capabilities]),
                    ),
                );
            },
            error: (err) => {
                console.error('Failed to fetch business domain options:', err);
            },
        });
    }

    private setCurrentUserAsFirstOwner(): void {
        this.authService.getUserName().subscribe({
            next: (user) => {
                const userGraph: UserGraph = {
                    id: user.objectId,
                    displayName: user.name,
                };
                this.currentUser.set(userGraph);
                this.createBusinessForm.controls.firstOwner.setValue(userGraph);
            },
            error: (err) => {
                console.error('Failed to fetch user name:', err);
            },
        });
    }
}
